/* eslint-disable max-lines -- workflows is a locked root; the kebab and right-click menus have to share one controller here. */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@heidi/design-system/alert-dialog';
import { Button } from '@heidi/design-system/button';
import { ContextMenuItem } from '@heidi/design-system/context-menu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { SidebarMenuAction } from '@heidi/design-system/page-shell';
import { toast } from '@heidi/design-system/toast';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { isTauri } from '@heidi/runtime/platform';
import type { NavigatePort } from '@heidi/runtime/shell';
import { useOverlayTriggerTooltip } from '@heidi/utils';
import { useUser } from '@heidi/workspace/data';
import { Ellipsis, MoreHorizontal, Share2, StepForward, Trash2 } from 'lucide-react';
import { type ComponentType, type JSX, type ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import {
  useDeleteWorkflow,
  useRoutineLaunchLock,
  useRunWorkflow,
} from '@heidi/agents/data';
import type { Workflow } from '../../../contracts/routines.types';
import { showRoutineChange } from '../../../data/workflows/components/routineChangeStore';
import { useOpenRunChat } from '../hooks/useOpenRunChat';
import {
  type RoutineTeamsSelectionProps,
  ShareWorkflowDialog,
} from './ShareWorkflowDialog';

/** What the menu needs from the surface that renders it. */
export interface RoutineActionsHost {
  navigate: NavigatePort;
  TeamsSelection: ComponentType<RoutineTeamsSelectionProps>;
  /** Class on the `trigger="sidebar"` kebab only. */
  sidebarActionClassName?: string;
}

export interface RoutineActionsMenuProps {
  workflow: Workflow;
  host: RoutineActionsHost;
  /**
   * Fired the moment Delete is confirmed — before the DELETE round-trip. Hosts
   * use it to get out of the way optimistically (the overview pane closes; the
   * run chat header leaves for the Routines surface). A failed delete leaves
   * the routine in place and says so via the error toast.
   */
  onDeleted?: () => void;
  /**
   * Additional routine actions supplied by the host, between Run now (when
   * shown) and Share. Destructive Delete stays last in the routine group.
   */
  routineMenuItems?: ReactNode;
  /** Run-level actions supplied by the host, after the group divider. */
  additionalMenuItems?: ReactNode;
  /**
   * Overview chrome already has a header Run — omit the duplicate menu item
   * there. Run-chat keeps it (re-run). Defaults on so other hosts stay as-is.
   */
  showRunNow?: boolean;
  /**
   * Overview chrome already has a header Delete — omit the duplicate on the
   * run-chat menu so only Delete chat remains. Defaults on so other hosts
   * stay as-is.
   */
  showDelete?: boolean;
  /**
   * Overview chrome already has Share — omit the duplicate on the run-chat
   * menu. Defaults on so other hosts stay as-is.
   */
  showShare?: boolean;
  /**
   * Skip returning focus to the ⋯ trigger on close. Rename uses this so the
   * in-place title input keeps the caret instead of losing it to the menu.
   */
  preventCloseAutoFocus?: boolean;
  /** Menu alignment against the trigger. */
  align?: 'start' | 'center' | 'end';
  /**
   * How the kebab renders. `overflow-button` is the overview/run-chat ⋯.
   * `sidebar` is the nav-v3 hover `SidebarMenuAction`. `none` is right-click
   * only (legacy Chat-sidebar routine rows).
   */
  trigger?: 'overflow-button' | 'sidebar' | 'none';
  /**
   * When set, the kebab is `action` and the same items are `contextItems` so
   * a host row can attach them to a right-click menu.
   */
  renderRow?: (slots: { action: ReactNode; contextItems: ReactNode }) => ReactNode;
}

/**
 * The routine ⋯ menu — Run now / Share / Delete — shared by the Routines-page
 * overview pane and the routine run chat header so both surfaces offer the
 * same actions behind the same guards (ownership, `can_schedule`, desktop-only
 * routines). Owns the delete confirmation and the share dialog.
 */
export function RoutineActionsMenu({
  workflow,
  host,
  onDeleted,
  routineMenuItems,
  additionalMenuItems,
  showRunNow = true,
  showDelete = true,
  showShare = true,
  preventCloseAutoFocus = false,
  align = 'end',
  trigger = 'overflow-button',
  renderRow,
}: RoutineActionsMenuProps): JSX.Element {
  const intl = useIntl();
  const workflowId = workflow.workflow_id;
  const { data: userRefId } = useUser((data) => data?.user_ref_id);
  const isOwner = Boolean(userRefId && workflow.owner_user_id === userRefId);
  const canDelete = showDelete && Boolean(isOwner && workflow.creator_type !== 'system');

  // Share stays owner-only; can_schedule is the BE owner capability
  // (fail-closed when unset).
  const canShare = showShare && Boolean(workflow.can_schedule);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  // Spent twice: as the aria-label and as the hover tooltip on the bare ⋯.
  const moreActionsLabel = intl.formatMessage({
    defaultMessage: 'More actions',
    id: '7ZbHb4JNtB',
    description: 'Accessible label on the routine overview ⋯ menu',
  });

  const openRunChat = useOpenRunChat(host.navigate);
  const runWorkflow = useRunWorkflow(workflowId);
  const deleteWorkflow = useDeleteWorkflow();
  const launchLock = useRoutineLaunchLock();
  const needsDesktopApp = Boolean(workflow.requires_desktop) && !isTauri();
  const handleRunNow = () => {
    if (!launchLock.tryBegin(workflowId)) return;
    const settle = () => launchLock.end(workflowId);
    runWorkflow.mutate(undefined, {
      onSuccess: (res) => {
        // The run IS the chat — leave the routines surface for its standalone
        // page. Shell-less runs (no project) have no chat to open; confirm
        // the kick-off instead of reading as a dead click.
        if (res.project_id) {
          // A cancelled route change rejects; settle regardless.
          void Promise.resolve(openRunChat(res.project_id))
            .catch(() => undefined)
            .finally(settle);
          return;
        }
        showRoutineChange(
          intl.formatMessage({
            defaultMessage: 'Run started',
            id: '+DOPIc6P7X',
            description: 'Confirmation chip when a run starts without a chat to open',
          })
        );
        settle();
      },
      onError: settle,
    });
  };
  const handleDelete = () => {
    // Get out of the way optimistically — the surface collapsing IS the
    // "deleted" feedback, and waiting on the DELETE round-trip left it hanging
    // open. If the request fails the routine stays in the list and the error
    // toast says why.
    setIsDeleteDialogOpen(false);
    onDeleted?.();
    deleteWorkflow.mutate(workflowId, {
      onSuccess: () =>
        showRoutineChange(
          intl.formatMessage({
            defaultMessage: 'Routine deleted',
            id: 'MwG3CRp9d7',
            description: 'Confirmation chip after deleting a routine',
          })
        ),
      onError: () =>
        toast({
          title: intl.formatMessage({
            defaultMessage: 'Failed to delete routine',
            id: 't0Tsahcxek',
            description: 'Error toast when deleting a routine fails',
          }),
          tone: 'destructive',
        }),
    });
  };

  const overflowTrigger =
    trigger === 'sidebar' ? (
      <SidebarMenuAction
        showOnHover
        aria-label={moreActionsLabel}
        data-testid="routine-actions-trigger"
        className={host.sidebarActionClassName}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <Ellipsis aria-hidden />
      </SidebarMenuAction>
    ) : (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconOnly
        className="text-text-primary"
        aria-label={moreActionsLabel}
        data-testid="routine-actions-trigger"
      >
        <MoreHorizontal aria-hidden />
      </Button>
    );

  type RoutineRowMenuItem = typeof DropdownMenuItem | typeof ContextMenuItem;
  const renderItems = (Item: RoutineRowMenuItem, includeHostItems: boolean) => (
    <>
      {showRunNow ? (
        <Item disabled={needsDesktopApp || runWorkflow.isPending} onSelect={handleRunNow}>
          <StepForward aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Run now',
            id: 'L5zPnXFGrB',
            description: 'Routine overview menu item: run the routine',
          })}
        </Item>
      ) : null}
      {includeHostItems ? routineMenuItems : null}
      {canShare ? (
        <Item onSelect={() => setIsShareDialogOpen(true)}>
          <Share2 aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Share',
            id: 'TKT0GAA6AU',
            description: 'Routine overview menu item: share the routine',
          })}
        </Item>
      ) : null}
      {canDelete ? (
        <Item
          variant="destructive"
          disabled={deleteWorkflow.isPending}
          onSelect={() => setIsDeleteDialogOpen(true)}
          data-testid="routine-actions-delete-item"
        >
          <Trash2 aria-hidden />
          {intl.formatMessage({
            defaultMessage: 'Delete',
            id: 'v5frcVo0At',
            description: 'Routine overview menu item: delete the routine',
          })}
        </Item>
      ) : null}
      {includeHostItems && additionalMenuItems ? (
        <>
          <DropdownMenuSeparator />
          {additionalMenuItems}
        </>
      ) : null}
    </>
  );

  const action =
    trigger === 'none' ? null : (
      <DropdownMenu>
        <Tooltip {...tooltipProps}>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>{overflowTrigger}</DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">{moreActionsLabel}</TooltipContent>
        </Tooltip>
        <DropdownMenuContent
          align={align}
          side="bottom"
          sideOffset={4}
          onClick={(event) => event.stopPropagation()}
          onCloseAutoFocus={(event) => {
            contentProps.onCloseAutoFocus();
            if (preventCloseAutoFocus) event.preventDefault();
          }}
        >
          {renderItems(DropdownMenuItem, true)}
        </DropdownMenuContent>
      </DropdownMenu>
    );

  const contextItems = renderItems(ContextMenuItem, false);

  const deleteDialog = canDelete ? (
    <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {intl.formatMessage({
              defaultMessage: 'Delete routine',
              id: 'CK97pQp8dc',
              description: 'Title of the routine delete confirmation dialog',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {intl.formatMessage(
              {
                defaultMessage:
                  'Are you sure you want to delete "{name}"? This action cannot be undone. Any active schedules and event triggers will also be removed.',
                id: '8DvCJlVw3A',
                description: 'Body of the routine delete confirmation dialog',
              },
              { name: workflow.name }
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>
            {intl.formatMessage({
              defaultMessage: 'Cancel',
              id: 'FwBYd+7RRD',
              description: 'Dismisses the routine delete confirmation',
            })}
          </AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteWorkflow.isPending}
            data-testid="routine-actions-delete-confirm"
          >
            {intl.formatMessage({
              defaultMessage: 'Delete',
              id: 'k8Eeh+8wFw',
              description: 'Routine delete confirmation action',
            })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  ) : null;

  return (
    <>
      {renderRow ? renderRow({ action, contextItems }) : action}
      {deleteDialog}
      {isShareDialogOpen ? (
        <ShareWorkflowDialog
          workflow={workflow}
          open={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          TeamsSelection={host.TeamsSelection}
        />
      ) : null}
    </>
  );
}
